import { Grid } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { RoutalCookiesBanner, RoutalLoader, RoutalPalette, RoutalTheme } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { IntlShape, RawIntlProvider } from 'react-intl';
import { Navigate, Route, Routes } from 'react-router-dom';

import { sessionService } from '../../application/SessionService';
import * as routes from '../../constants/Routing';
import CustomRouter from '../../helpers/CustomRouter';
import { intl } from '../../helpers/IntGlobalProvider';
import { useRetrieveTranslations } from '../../hooks/translations/useRetrieveTranslations';
import { useSetActiveLanguage } from '../../hooks/translations/useSetActiveLanguage';
import { useRetrieveUser } from '../../hooks/user/useRetrieveUser';
import Text from '../../react-components/Text';
import { PrivateComponent } from './PrivateComponent';
import PublicComponent from './PublicComponent';
import { SnackbarComponent } from './SnackbarComponent';

const PlannerRoutes = () => {
  return (
    <Routes>
      <Route path="/logout" element={<Navigate to="/login" />} />
      <Route path="/h/*" element={<PrivateComponent />} />
      <Route path="/*" element={<PublicComponent />} />
    </Routes>
  );
};

export const Main = () => {
  const { activeLanguage } = useRetrieveTranslations();
  const isLogged = sessionService.isLogged();
  const { data: user } = useRetrieveUser();
  const { run: setActiveLocale } = useSetActiveLanguage();
  const tempCookiesAccepted = localStorage.getItem(`COOKIES_ACCEPTED`);
  const [cookiesAccepted, setCookiesAccepted] = useState<boolean>(tempCookiesAccepted === `true` ? true : false);
  const [defaultIntl, setDefaultIntl] = useState<IntlShape>(intl);

  useEffect(() => {
    if (activeLanguage) {
      setDefaultIntl(activeLanguage);
    }
  }, [activeLanguage]);

  useEffect(() => {
    if (isLogged) {
      localStorage.setItem(`COOKIES_ACCEPTED`, JSON.stringify(true));
      setCookiesAccepted(true);
    }

    if (user?.language) {
      // Setting the user language;
      setActiveLocale({ language: user!.language });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogged, user?.language]);

  const handleCookiesAccept = () => {
    localStorage.setItem(`COOKIES_ACCEPTED`, JSON.stringify(true));
    setCookiesAccepted(true);
  };

  if (!activeLanguage) {
    return (
      <Grid
        item
        container
        direction="column"
        style={{ height: `100vh`, width: `100vw` }}
        alignItems="center"
        justifyContent="center"
      >
        <RoutalLoader />
      </Grid>
    );
  }

  return (
    <ThemeProvider theme={RoutalTheme}>
      <RawIntlProvider value={defaultIntl}>
        <>
          <SnackbarComponent />
          <CustomRouter>
            <PlannerRoutes />
          </CustomRouter>
          {!isLogged && !cookiesAccepted && (
            <RoutalCookiesBanner
              message={
                <Text
                  tagName="span"
                  intlId="cookies.message"
                  intlValues={{
                    a: (chunks: any) => (
                      <a href={`${routes.COOKIES_POLICY}`} target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </a>
                    ),
                  }}
                  style={{ color: RoutalPalette.white }}
                />
              }
              onClose={handleCookiesAccept}
              closeText={<Text style={{ color: RoutalPalette.white, cursor: `pointer` }} intlId="confirm" />}
            />
          )}
        </>
      </RawIntlProvider>
    </ThemeProvider>
  );
};
